/* Whether MEXC is being read on this device, and if not, why.
 *
 * The screen said "entered by hand" in every case that was not a success or a
 * recorded failure — and most of the ways the sync stops never recorded
 * anything: no key pasted on this device, reading switched off, no account to
 * file it under, a browser tab that cannot reach the exchange. Each returned
 * before trying, so the phone showed the same words whether the key was
 * missing or the exchange was down. This names which it is. */

import { brokerById, connectionFor, missingFields } from '../lib/brokers';
import type { AccountRecord } from '../types';

export type MexcStatus =
  | 'no-key'       // nothing pasted on this device
  | 'off'          // pasted, automatic reading switched off
  | 'no-account'   // pasted, but no account to file it under
  | 'browser'      // a browser tab, which cannot reach the exchange
  | 'failing'      // tried, and the last try failed
  | 'waiting'      // ready, and not read yet
  | 'reading';     // read, and the last read worked

type Settings = Parameters<typeof connectionFor>[0] & {
  brokerAccounts?: Record<string, string>;
  mexcAuto?: boolean;
  mexcError?: string;
  mexcSyncedAt?: string;
};

/** In the order the sync itself checks, so the reason given is the one that
    actually stopped it. */
export function mexcStatus(settings: Settings, accounts: AccountRecord[], canReach: boolean): MexcStatus {
  const def = brokerById('mexc');
  if (!def || missingFields(def, connectionFor(settings, 'mexc')).length > 0) return 'no-key';
  if (settings.mexcAuto === false) return 'off';
  const account = settings.brokerAccounts?.['mexc'] ?? accounts.find((a) => a.isFutures)?.id;
  if (!account) return 'no-account';
  if (!canReach) return 'browser';
  if (settings.mexcError) return 'failing';
  return settings.mexcSyncedAt ? 'reading' : 'waiting';
}
